import type { Tick } from "../types";
import { compositeSource, DerivHistorySource, TickSource } from "./TickSource";

export type ReplayListener = (ticks: Tick[], initial: boolean) => void;

export interface ReplayState {
  active: boolean;
  sessionId: number;
  symbol: string;
  startTime: number;
  endTime: number; // live edge (never emit beyond)
  cursor: number; // time of last emitted tick
  playing: boolean;
  speed: number;
  loading: boolean;
  emitted: number; // ticks emitted since startTime
  loaded: number; // ticks loaded since startTime
  warmup: number;
  finished: boolean;
  error: string | null;
}

const SPEEDS = [0.25, 0.5, 1, 2, 5, 10];
const PREFETCH_AT = 2500;
const LOOP_MS = 40;

const idle = (): ReplayState => ({
  active: false, sessionId: 0, symbol: "", startTime: 0, endTime: 0, cursor: 0, playing: false, speed: 1,
  loading: false, emitted: 0, loaded: 0, warmup: 0, finished: false, error: null,
});

// A "fake Deriv feed": replays historical ticks onto the same bus the live feed uses.
// No look-ahead: only ticks with time <= cursor are ever delivered; warm-up ticks are strictly < startTime.
export class ReplayEngine {
  state: ReplayState = idle();
  private ticks: Tick[] = [];
  private idx = 0; // next tick to emit
  private loadedUpTo = 0;
  private listeners = new Set<ReplayListener>();
  private stateListeners = new Set<(s: ReplayState) => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private anchorReal = 0;
  private anchorVirtual = 0;
  private source: TickSource = DerivHistorySource;
  private fetching: Promise<void> | null = null;

  static SPEEDS = SPEEDS;

  setFallbackSource(fallback: TickSource | null) {
    this.source = compositeSource(DerivHistorySource, fallback);
  }

  onState(cb: (s: ReplayState) => void): () => void {
    this.stateListeners.add(cb);
    cb(this.state);
    return () => this.stateListeners.delete(cb);
  }

  private set(patch: Partial<ReplayState>) {
    this.state = { ...this.state, ...patch };
    this.stateListeners.forEach((cb) => cb(this.state));
  }

  isReplaying(symbol: string) { return this.state.active && this.state.symbol === symbol; }

  // ---- session ---------------------------------------------------------------------
  async start(symbol: string, startTime: number, warmupSeconds = 3600) {
    this.pause();
    const sessionId = this.state.sessionId + 1;
    const endTime = Math.floor(Date.now() / 1000) - 3;
    startTime = Math.min(Math.floor(startTime), endTime - 10);
    this.ticks = []; this.idx = 0; this.loadedUpTo = startTime - 1;
    this.set({ ...idle(), active: true, sessionId, symbol, startTime, endTime, cursor: startTime - 1, loading: true, speed: this.state.speed || 1 });
    try {
      const warm = await this.source.before(symbol, startTime, Math.min(5000, warmupSeconds));
      if (this.state.sessionId !== sessionId) return;
      this.ticks = warm.filter((t) => t.time < startTime);
      this.idx = this.ticks.length;
      this.set({ warmup: this.ticks.length, cursor: startTime - 1 });
      await this.fetchMore(sessionId);
      if (this.state.sessionId !== sessionId) return;
      this.set({ loading: false });
      this.listeners.forEach((l) => l(this.ticks.slice(0, this.idx), true));
    } catch (e: any) {
      this.set({ loading: false, error: e?.message || "failed to load history" });
    }
  }

  stop() {
    this.pause();
    this.ticks = []; this.idx = 0;
    this.set({ ...idle(), sessionId: this.state.sessionId, speed: this.state.speed });
  }

  private async fetchMore(sessionId: number) {
    if (this.fetching) return this.fetching;
    if (this.loadedUpTo >= this.state.endTime) return;
    this.fetching = (async () => {
      try {
        const batch = await this.source.forward(this.state.symbol, this.loadedUpTo + 1, this.state.endTime);
        if (this.state.sessionId !== sessionId) return;
        const fresh = batch.filter((t) => t.time > this.loadedUpTo && t.time <= this.state.endTime);
        if (!fresh.length) { this.loadedUpTo = this.state.endTime; }
        else { this.ticks.push(...fresh); this.loadedUpTo = fresh[fresh.length - 1].time; }
        this.set({ loaded: this.ticks.length - this.state.warmup });
      } finally { this.fetching = null; }
    })();
    return this.fetching;
  }

  // ---- consumers (MarketDataEngine delegates here while replaying) ------------------
  subscribe(listener: ReplayListener, backfill: number): () => void {
    this.listeners.add(listener);
    if (backfill > 0 && !this.state.loading) {
      const upTo = this.ticks.slice(0, this.idx);
      listener(backfill >= upTo.length ? upTo : upTo.slice(-backfill), true);
    }
    return () => this.listeners.delete(listener);
  }

  private emit(ticks: Tick[]) {
    if (!ticks.length) return;
    this.listeners.forEach((l) => l(ticks, false));
  }

  private resetTo(idx: number) {
    this.idx = Math.max(this.state.warmup, Math.min(idx, this.ticks.length));
    const cursor = this.idx > 0 ? this.ticks[this.idx - 1].time : this.state.startTime - 1;
    this.set({ cursor, emitted: Math.max(0, this.idx - this.state.warmup), finished: false });
    this.anchorVirtual = cursor; this.anchorReal = performance.now();
    this.listeners.forEach((l) => l(this.ticks.slice(0, this.idx), true));
  }

  // ---- transport controls -----------------------------------------------------------
  play() {
    if (!this.state.active || this.state.playing || this.state.loading) return;
    this.anchorReal = performance.now();
    this.anchorVirtual = this.idx < this.ticks.length ? this.ticks[this.idx].time - 0.001 : this.state.cursor;
    this.set({ playing: true, finished: false });
    this.loop();
  }

  pause() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.state.playing) this.set({ playing: false });
  }

  setSpeed(speed: number) {
    this.anchorVirtual = this.virtualNow();
    this.anchorReal = performance.now();
    this.set({ speed });
  }

  private virtualNow() {
    return this.anchorVirtual + ((performance.now() - this.anchorReal) / 1000) * this.state.speed;
  }

  private loop() {
    if (!this.state.playing) return;
    const vnow = this.virtualNow();
    const batch: Tick[] = [];
    while (this.idx < this.ticks.length && this.ticks[this.idx].time <= vnow) batch.push(this.ticks[this.idx++]);
    if (batch.length) {
      this.emit(batch);
      this.set({ cursor: batch[batch.length - 1].time, emitted: this.idx - this.state.warmup });
    }
    if (this.ticks.length - this.idx < PREFETCH_AT && this.loadedUpTo < this.state.endTime) this.fetchMore(this.state.sessionId);
    if (this.idx >= this.ticks.length && this.loadedUpTo >= this.state.endTime) { this.set({ playing: false, finished: true }); return; }
    this.timer = setTimeout(() => this.loop(), LOOP_MS);
  }

  stepTick() {
    if (!this.state.active || this.state.loading) return;
    this.pause();
    if (this.idx >= this.ticks.length) { this.fetchMore(this.state.sessionId); return; }
    const t = this.ticks[this.idx++];
    this.emit([t]);
    this.set({ cursor: t.time, emitted: this.idx - this.state.warmup });
    if (this.ticks.length - this.idx < PREFETCH_AT) this.fetchMore(this.state.sessionId);
  }

  // Advance until a new candle opens (time bucket changes, or N ticks for tick bars).
  stepCandle(tfSeconds: number | null, tfTicks: number | null) {
    if (!this.state.active || this.state.loading || this.idx >= this.ticks.length) return;
    this.pause();
    const batch: Tick[] = [];
    if (tfTicks) { while (this.idx < this.ticks.length && batch.length < tfTicks) batch.push(this.ticks[this.idx++]); }
    else {
      const s = tfSeconds || 1;
      const bucket = Math.floor(this.ticks[this.idx].time / s);
      while (this.idx < this.ticks.length && Math.floor(this.ticks[this.idx].time / s) === bucket) batch.push(this.ticks[this.idx++]);
      if (this.idx < this.ticks.length) batch.push(this.ticks[this.idx++]); // first tick of the next candle
    }
    this.emit(batch);
    if (batch.length) this.set({ cursor: batch[batch.length - 1].time, emitted: this.idx - this.state.warmup });
    if (this.ticks.length - this.idx < PREFETCH_AT) this.fetchMore(this.state.sessionId);
  }

  stepBack(tfSeconds: number | null, tfTicks: number | null) {
    if (!this.state.active || this.state.loading) return;
    this.pause();
    if (tfTicks) { this.resetTo(this.idx - tfTicks); return; }
    const s = tfSeconds || 1;
    const cur = this.idx > 0 ? this.ticks[this.idx - 1].time : this.state.startTime;
    const target = Math.floor(cur / s) * s - 1; // end of previous candle
    let i = this.idx;
    while (i > this.state.warmup && this.ticks[i - 1].time > target) i--;
    this.resetTo(i);
  }

  restart() { if (this.state.active && !this.state.loading) { this.pause(); this.resetTo(this.state.warmup); } }

  // Jump to an absolute time. Inside the loaded range → instant reset; otherwise a fresh session.
  seek(time: number) {
    if (!this.state.active || this.state.loading) return;
    const wasPlaying = this.state.playing;
    this.pause();
    if (time >= this.state.startTime && time <= this.loadedUpTo) {
      let i = this.state.warmup;
      while (i < this.ticks.length && this.ticks[i].time <= time) i++;
      this.resetTo(i);
      if (wasPlaying) this.play();
    } else {
      this.start(this.state.symbol, time).then(() => { if (wasPlaying) this.play(); });
    }
  }

  get progress(): { from: number; to: number; cursor: number; loadedUpTo: number } {
    return { from: this.state.startTime, to: this.state.endTime, cursor: this.state.cursor, loadedUpTo: this.loadedUpTo };
  }
}

let instance: ReplayEngine | null = null;
export function getReplay(): ReplayEngine {
  if (!instance) instance = new ReplayEngine();
  return instance;
}
